import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Eye, CheckCircle2, ShieldAlert, Clock, RefreshCw, X, Lock, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ExternalLink, Activity, History, CircleAlert, Zap, CircleEllipsis, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { automationApi } from '../../../services/automationapi';
import { useClientLevel } from '../../../hooks/useClientLevel';
import { CustomSelect } from './CustomSelect';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';
import toast from 'react-hot-toast';

interface Alert {
  id: number;
  rule_id: number;
  rule_name?: string;
  client_id?: number | null;
  client_name?: string | null;
  organization_id?: number | null;
  asset_id?: number | null;
  asset_name?: string | null;
  asset_os_name?: string | null;
  asset_os_version?: string | null;
  collection?: string;
  record_key?: string;
  severity?: 'info' | 'warning' | 'critical' | string;
  status?: 'open' | 'acknowledged' | 'resolved' | 'suppressed' | 'grouped' | string;
  title?: string;
  message?: string;
  source?: string;
  signal_type?: string;
  entity_key?: string;
  occurrence_count?: number;
  first_seen?: string;
  last_seen?: string;
  opened_at?: string;
  last_fired_at?: string;
  resolved_at?: string | null;
  created?: string;
}

interface ActionLog {
  id: number;
  action_id: string;
  action_type: string;
  phase: 'fire' | 'resolve' | 'escalate' | string;
  status: 'success' | 'failed' | 'skipped' | 'pending_approval' | string;
  detail: string;
  response: string;
  duration_ms: number;
  created: string;
}

interface AlertDetail extends Alert {
  context?: {
    signal?: any;
    asset?: any;
    entity?: any;
    event?: any;
    changes?: any;
    record_key?: any;
    collection?: any;
    asset_id?: any;
    organization_id?: any;
    last_signal?: any;
  };
  escalated_at?: string | null;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  resolved_by?: string | null;
  dedup_key?: string;
  action_logs?: ActionLog[];
  timeline?: Array<{
    at?: string;
    timestamp?: string;
    status?: string;
    message?: string;
    detail?: string;
    event?: string;
  }>;
  flow_run?: {
    status: 'running' | 'waiting' | 'waiting_approval' | 'completed' | 'stopped' | 'failed' | string;
    current_step: string;
    wake_at: string | null;
    continue_only_if: 'always' | 'still_open' | 'resolved' | null | string;
    detail: string;
  } | null;
}

const renderSafeContent = (val: any): string => {
  if (!val) return '';
  if (typeof val === 'object') {
    try { return JSON.stringify(val, null, 2); } catch { return String(val); }
  }
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      if (typeof parsed === 'object' && parsed !== null) {
        return JSON.stringify(parsed, null, 2);
      }
    } catch { }
  }
  return String(val);
};
const formatText = (text: string) =>
  text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const toUTCStartOfDay = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const localDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 0, 0, 0, 0);
      return localDate.toISOString();
    }
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  } catch {
    return dateStr;
  }
};

const toUTCEndOfDay = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const localDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 23, 59, 59, 999);
      return localDate.toISOString();
    }
    const d = new Date(dateStr);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  } catch {
    return String(dateStr);
  }
};

const getActionLogIcon = (actionType?: string) => {
  console.log("actionType", actionType)
  const imgClass = "w-3.5 h-3.5 object-contain select-none pointer-events-none shrink-0";
  const typeLower = (actionType || '').toLowerCase();
  switch (typeLower) {
    case 'whatsapp':
      return <img src="/whatsapp.png" className={imgClass} alt="WhatsApp" />;
    case 'slack':
      return (
        <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none">
          <path d="M5.04 15.12a2.52 2.52 0 1 1-2.52-2.52h2.52v2.52zM6.3 15.12a2.52 2.52 0 0 1 5.04 0v5.04a2.52 2.52 0 1 1-5.04 0v-5.04z" fill="#36C5F0" />
          <path d="M8.88 5.04a2.52 2.52 0 1 1 2.52-2.52v2.52H8.88zM8.88 6.3a2.52 2.52 0 0 1 0 5.04H3.84a2.52 2.52 0 1 1 0-5.04h5.04z" fill="#2EB67D" />
          <path d="M18.96 8.88a2.52 2.52 0 1 1 2.52 2.52h-2.52V8.88zM17.7 8.88a2.52 2.52 0 0 1-5.04 0V3.84a2.52 2.52 0 1 1 5.04 0v5.04z" fill="#ECB22E" />
          <path d="M15.12 18.96a2.52 2.52 0 1 1-2.52 2.52v-2.52h2.52zM15.12 17.7a2.52 2.52 0 0 1 0-5.04h5.04a2.52 2.52 0 1 1 0 5.04h-5.04z" fill="#E01E5A" />
        </svg>
      );
    case 'email':
      return <img src="/email_workflow.png" className={imgClass} alt="Email" />;
    case 'webhook':
      return <img src="/webhook.svg" className={imgClass} alt="Webhook" />;
    case 'teams':
    case 'microsoft_teams':
      return <img src="/teams.png" className={imgClass} alt="Teams" />;
    case 'sms':
      return <img src="/sms.png" className={imgClass} alt="SMS" />;
    case 'telegram':
      return <img src="/telegram.png" className={imgClass} alt="Telegram" />;
    case 'ticket':
    case 'ticketing':
      return <img src="/rdp_logo.png" className={imgClass} alt="Ticketing" />;
    case 'delay':
      return (
        <svg className="w-3.5 h-3.5 text-blue-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      );
    case 'script':
      return (
        <svg className="w-3.5 h-3.5 text-orange-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="4 17 10 11 4 5" />
          <line x1="12" y1="19" x2="20" y2="19" />
        </svg>
      );
    case 'event':
      return (
        <svg className="w-3.5 h-3.5 text-emerald-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
    case 'alert':
      return (
        <svg className="w-3.5 h-3.5 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      )
    default:
      return (
        <svg className="w-3.5 h-3.5 text-teal-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
        </svg>
      );
  }
};

const getActionFormattedTitle = (type?: string) => {
  if (!type) return 'Action';
  const typeLower = type.toLowerCase();
  switch (typeLower) {
    case 'whatsapp': return 'WhatsApp';
    case 'slack': return 'Slack';
    case 'email': return 'Email';
    case 'webhook': return 'Webhook';
    case 'teams':
    case 'microsoft_teams': return 'Microsoft Teams';
    case 'sms': return 'SMS';
    case 'telegram': return 'Telegram';
    case 'delay': return 'Delay';
    case 'ticket':
    case 'ticketing': return 'Ticket';
    case 'script': return 'Run Script';
    case 'event': return 'Trigger Event';
    default: return type ? type.replace(/_/g, ' ').toUpperCase() : 'Action';
  }
};

const renderAlertTimeline = (timeline?: Array<{ at?: string; timestamp?: string; status?: string; message?: string; detail?: string; event?: string }>) => {
  if (!timeline || timeline.length === 0) return null;

  const getTimelineStatusBadge = (status?: string) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'open':
      case 'opened':
      case 'firing':
        return {
          bg: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30',
          dot: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]',
          icon: <CircleAlert className="w-3 h-3 text-red-500 dark:text-red-400 shrink-0" />,
          label: 'OPEN'
        };
      case 'closed':
      case 'resolved':
        return {
          bg: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/30',
          dot: 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]',
          icon: <CheckCircle2 className="w-3 h-3 text-green-600 dark:text-green-400 shrink-0" />,
          label: s === 'closed' ? 'CLOSED' : 'RESOLVED'
        };
      case 'acknowledged':
      case 'ack':
        return {
          bg: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/30',
          dot: 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]',
          icon: <Clock className="w-3 h-3 text-yellow-600 dark:text-yellow-400 shrink-0" />,
          label: 'ACKNOWLEDGED'
        };
      default:
        return {
          bg: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
          dot: 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]',
          icon: <Activity className="w-3 h-3 text-blue-600 dark:text-blue-400 shrink-0" />,
          label: (status || 'EVENT').toUpperCase()
        };
    }
  };

  return (
    <div className="space-y-2.5 pt-3 border-t border-gray-200 dark:border-gray-800/80">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <History className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
          <h5 className="text-[10px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Alert Lifecycle Timeline</h5>
        </div>
        <span className="text-[9px] font-mono font-bold text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-gray-900 border border-orange-200 dark:border-gray-800 px-2 py-0.5 rounded-full">
          {timeline.length} events
        </span>
      </div>

      <div className="relative pl-6 space-y-3 before:absolute before:left-[11px] before:top-3 before:bottom-3 before:w-[2px] before:bg-gradient-to-b before:from-orange-500/50 before:via-gray-300 dark:before:via-gray-700 before:to-gray-200 dark:before:to-gray-800/50">
        {timeline.map((item, idx) => {
          const config = getTimelineStatusBadge(item.status);
          const timeStr = item.at || item.timestamp;
          const formattedTime = formatDateTime(timeStr);

          return (
            <div key={idx} className="relative group">
              {/* Single Centered Circular Dot */}
              <div className={`absolute -left-[18px] top-[13px] w-3 h-3 rounded-full ${config.dot} transition-transform duration-150 group-hover:scale-125 z-10`} />

              {/* Event Card */}
              <div className="bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-800/80 hover:border-gray-300 dark:hover:border-gray-700 rounded-lg p-2.5 transition-all space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    {config.icon}
                    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${config.bg}`}>
                      {config.label}
                    </span>
                  </div>
                  <span className="text-[9px] font-mono text-gray-500 dark:text-gray-200">
                    {formattedTime}
                  </span>
                </div>

                {item.message && (
                  <p className="text-[11px] text-gray-900 dark:text-gray-200 font-medium leading-relaxed whitespace-pre-wrap break-words">
                    {typeof item.message === 'string' ? item.message.replace(/\\n/g, '\n') : item.message}
                  </p>
                )}

                {item.detail && (
                  <p className="text-[10px] text-gray-700 dark:text-gray-200 font-mono bg-gray-100 dark:bg-gray-950/60 p-1.5 rounded border border-gray-200 dark:border-gray-900 break-words whitespace-pre-wrap">
                    {typeof item.detail === 'string' ? item.detail.replace(/\\n/g, '\n') : item.detail}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const renderCleanLogResponse = (responseStr?: string | object) => {
  if (!responseStr) return null;
  let obj: any = null;
  if (typeof responseStr === 'object') {
    obj = responseStr;
  } else {
    try {
      obj = JSON.parse(responseStr);
    } catch {
      obj = responseStr;
    }
  }

  if (!obj) return null;

  if (typeof obj !== 'object') {
    return (
      <div className="mt-1.5 text-[10px] font-mono text-gray-800 dark:text-gray-300 bg-gray-100 dark:bg-gray-950/80 px-2 py-1 rounded border border-gray-200 dark:border-gray-800 break-all select-all">
        {String(obj)}
      </div>
    );
  }

  // 1. Specialized rendering for Slack
  if (obj.ts !== undefined && (obj.channel || obj.message || obj.ok !== undefined)) {
    const isOk = obj.ok === true;
    const channel = obj.channel || obj.message?.channel;
    const textMsg = obj.message?.text || obj.text || obj.message?.blocks?.[0]?.elements?.[0]?.elements?.[0]?.text;
    const botName = obj.message?.bot_profile?.name || obj.bot_profile?.name || 'Slack Bot';
    const botAvatar = obj.message?.bot_profile?.icons?.image_48 || obj.message?.bot_profile?.icons?.image_36 || obj.bot_profile?.icons?.image_72;

    return (
      <div className="mt-2 space-y-2 bg-slate-50 dark:bg-[#1a1d21]/90 border border-slate-200 dark:border-slate-700/60 rounded-xl p-2.5 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold text-sky-600 dark:text-sky-400 uppercase tracking-wider flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none">
              <path d="M5.04 15.12a2.52 2.52 0 1 1-2.52-2.52h2.52v2.52zM6.3 15.12a2.52 2.52 0 0 1 5.04 0v5.04a2.52 2.52 0 1 1-5.04 0v-5.04z" fill="#36C5F0" />
              <path d="M8.88 5.04a2.52 2.52 0 1 1 2.52-2.52v2.52H8.88zM8.88 6.3a2.52 2.52 0 0 1 0 5.04H3.84a2.52 2.52 0 1 1 0-5.04h5.04z" fill="#2EB67D" />
              <path d="M18.96 8.88a2.52 2.52 0 1 1 2.52 2.52h-2.52V8.88zM17.7 8.88a2.52 2.52 0 0 1-5.04 0V3.84a2.52 2.52 0 1 1 5.04 0v5.04z" fill="#ECB22E" />
              <path d="M15.12 18.96a2.52 2.52 0 1 1-2.52 2.52v-2.52h2.52zM15.12 17.7a2.52 2.52 0 0 1 0-5.04h5.04a2.52 2.52 0 1 1 0 5.04h-5.04z" fill="#E01E5A" />
            </svg>
            Slack Notification
          </span>
          {isOk && (
            <span className="text-[9px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 dark:border-emerald-500/30 text-emerald-600 dark:text-emerald-300">
              Delivered
            </span>
          )}
        </div>

        <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-200 dark:border-slate-700/40">
          <div className="flex items-center gap-2">
            {botAvatar ? (
              <img src={botAvatar} className="w-4 h-4 rounded object-cover" alt="" />
            ) : (
              <div className="w-4 h-4 rounded bg-purple-500/20 text-purple-600 dark:text-purple-300 font-bold text-[9px] flex items-center justify-center">
                S
              </div>
            )}
            <span className="font-semibold text-gray-800 dark:text-gray-200 text-[11px]">{botName}</span>
          </div>

          {channel && (
            <span className="font-mono text-[10px] text-sky-600 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/60 border border-sky-200 dark:border-sky-500/20 px-2 py-0.5 rounded-md font-semibold">
              #{channel}
            </span>
          )}
        </div>

        {textMsg && (
          <div className="mt-1 bg-white dark:bg-[#0f1115] border border-slate-200 dark:border-slate-800/80 rounded-lg p-2 text-[11px] text-gray-800 dark:text-gray-200 font-sans leading-relaxed whitespace-pre-wrap break-words border-l-2 border-l-sky-500">
            {textMsg}
          </div>
        )}
      </div>
    );
  }

  // 2. Specialized rendering for Microsoft Teams
  if (obj.activityId || (typeof obj.id === 'string' && (obj.id.includes('tacv2') || obj.id.includes('thread')))) {
    return (
      <div className="mt-2 space-y-2 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-500/20 rounded-xl p-2.5 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider flex items-center gap-1.5">
            <img src="/teams.png" className="w-3.5 h-3.5 object-contain" alt="" />
            Microsoft Teams Message
          </span>
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-indigo-500/10 dark:bg-indigo-500/20 border border-indigo-500/20 dark:border-indigo-500/30 text-indigo-600 dark:text-indigo-300 uppercase">
            Delivered
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] pt-1 border-t border-indigo-200 dark:border-indigo-500/10">
          {obj.activityId && (
            <div className="bg-white dark:bg-gray-950/60 p-1.5 rounded-lg border border-indigo-100 dark:border-indigo-500/10">
              <span className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider block">Activity ID</span>
              <span className="font-mono text-[10px] text-indigo-600 dark:text-indigo-300">{obj.activityId}</span>
            </div>
          )}
          {obj.id && (
            <div className="bg-white dark:bg-gray-950/60 p-1.5 rounded-lg border border-indigo-100 dark:border-indigo-500/10 min-w-0">
              <span className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider block">Message ID</span>
              <span className="font-mono text-[10px] text-indigo-600 dark:text-indigo-300 truncate block" title={obj.id}>{obj.id}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 3. Specialized rendering for WhatsApp
  if (obj.messaging_product === 'whatsapp' || obj.messages || (obj.contacts && obj.contacts.length > 0)) {
    const waId = obj.contacts?.[0]?.wa_id || obj.contacts?.[0]?.input;
    const msgId = obj.messages?.[0]?.id;
    const msgStatus = obj.messages?.[0]?.message_status;

    return (
      <div className="mt-2 space-y-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-500/20 rounded-xl p-2.5 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
            <img src="/whatsapp.png" className="w-3.5 h-3.5 object-contain" alt="" />
            WhatsApp Notification
          </span>
          {msgStatus && (
            <span className="text-[9px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 dark:border-emerald-500/30 text-emerald-600 dark:text-emerald-300">
              {msgStatus}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] pt-1 border-t border-emerald-200 dark:border-emerald-500/10">
          {waId && (
            <div className="bg-white dark:bg-gray-950/60 p-1.5 rounded-lg border border-emerald-100 dark:border-emerald-500/10">
              <span className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider block">Recipient</span>
              <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-200">+{waId}</span>
            </div>
          )}
          {msgId && (
            <div className="bg-white dark:bg-gray-950/60 p-1.5 rounded-lg border border-emerald-100 dark:border-emerald-500/10 min-w-0">
              <span className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider block">Message ID</span>
              <span className="font-mono text-[10px] text-emerald-600 dark:text-emerald-300 truncate block" title={msgId}>{msgId}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 4. Specialized rendering for Ticket
  if (obj.ticket_number || obj.ticket_id || obj.ticket_url) {
    const ticketNo = obj.ticket_number || `#${obj.ticket_id}`;
    let ticketUrl = obj.ticket_url || obj.url;
    if (ticketUrl && (obj.ticket_id || obj.id)) {
      const tid = obj.ticket_id || obj.id;
      if (!ticketUrl.includes('/tickets/')) {
        ticketUrl = `${ticketUrl.replace(/\/+$/, '')}/tickets/${tid}`;
      }
    }

    return (
      <div className="mt-2 space-y-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-500/20 rounded-xl p-2.5 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
            <img src="/rdp_logo.png" className="w-3.5 h-3.5 object-contain" alt="" />
            Support Ticket
          </span>
          {ticketUrl ? (
            <a
              href={ticketUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[10px] font-mono font-bold text-orange-600 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 px-2 py-0.5 rounded-md flex items-center gap-1.5 transition-all hover:bg-orange-100 dark:hover:bg-orange-500/20"
            >
              <span>{ticketNo}</span>
              <ExternalLink className="w-3 h-3 text-orange-500 dark:text-orange-400" />
            </a>
          ) : (
            <span className="text-[10px] font-mono font-bold text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 px-2 py-0.5 rounded-md">
              {ticketNo}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] pt-1 border-t border-blue-200 dark:border-blue-500/10 text-gray-700 dark:text-gray-300">
          {obj.ticket_id && (
            <div>
              <span className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Ticket ID: </span>
              <span className="font-mono font-semibold text-blue-700 dark:text-blue-200">#{obj.ticket_id}</span>
            </div>
          )}
          {obj.ticket_number && (
            <div>
              <span className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Number: </span>
              <span className="font-mono font-semibold text-blue-700 dark:text-blue-200">{obj.ticket_number}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 5. Specialized rendering for Alert action note
  if (obj.note && Object.keys(obj).length === 1) {
    return (
      <div className="mt-2 text-[11px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 dark:bg-amber-400 animate-pulse"></span>
        <span>{obj.note}</span>
      </div>
    );
  }

  // Helper to format values recursively without [object Object]
  const renderValue = (val: any): React.ReactNode => {
    if (val === null || val === undefined) return <span className="text-gray-400 dark:text-gray-500 font-mono">null</span>;
    if (typeof val === 'boolean') return <span className="font-mono text-purple-600 dark:text-purple-400">{val ? 'true' : 'false'}</span>;
    if (typeof val === 'number') return <span className="font-mono text-cyan-600 dark:text-cyan-400">{val}</span>;
    if (typeof val === 'string') {
      if (val.startsWith('http://') || val.startsWith('https://')) {
        return (
          <a
            href={val}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-orange-600 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 underline font-mono flex items-center gap-1 truncate"
            title={val}
          >
            <span className="truncate">{val}</span>
            <ExternalLink className="w-3 h-3 text-orange-500 dark:text-orange-400 flex-shrink-0" />
          </a>
        );
      }
      return <span className="text-gray-800 dark:text-gray-200">{val}</span>;
    }
    if (Array.isArray(val)) {
      return (
        <div className="space-y-1 pt-0.5">
          {val.map((item, idx) => (
            <div key={idx} className="text-[10px] font-mono text-gray-800 dark:text-gray-300 bg-gray-100 dark:bg-gray-900/60 p-1 rounded border border-gray-200 dark:border-gray-800">
              {typeof item === 'object' ? JSON.stringify(item) : String(item)}
            </div>
          ))}
        </div>
      );
    }
    if (typeof val === 'object') {
      return (
        <pre className="text-[9px] font-mono text-gray-800 dark:text-gray-300 bg-gray-100 dark:bg-gray-950 p-1.5 rounded border border-gray-200 dark:border-gray-800 overflow-x-auto">
          {JSON.stringify(val, null, 2)}
        </pre>
      );
    }
    return String(val);
  };

  const entries = Object.entries(obj).filter(([_, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return null;

  return (
    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
      {entries.map(([key, val]) => (
        <div key={key} className="bg-white dark:bg-gray-950/70 p-2 rounded-lg border border-gray-200 dark:border-gray-800/80 space-y-0.5 min-w-0">
          <span className="text-[8px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider block truncate">
            {key.replace(/_/g, ' ')}
          </span>
          <div className="text-[10px] font-mono font-medium text-orange-600 dark:text-orange-300 truncate" title={typeof val === 'string' ? val : undefined}>
            {renderValue(val)}
          </div>
        </div>
      ))}
    </div>
  );
};

export const AlertsTab: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortBy = searchParams.get('sort_by') || '';
  const sortOrder = (searchParams.get('order') || searchParams.get('sort_order') || 'asc') as 'asc' | 'desc';

  const clientLevel = useClientLevel();
  const isGlobalLevel = !clientLevel?.id || clientLevel.name === 'global';

  const handleSort = (field: string) => {
    let nextOrder: 'asc' | 'desc' = 'asc';
    if (sortBy === field) {
      nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('sort_by', field);
      next.set('order', nextOrder);
      return next;
    }, { replace: true });
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-gray-500 transition-opacity" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="w-2.5 h-2.5 ml-1 text-orange-500" />
      : <ArrowDown className="w-2.5 h-2.5 ml-1 text-orange-500" />;
  };

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedAlertId, setSelectedAlertId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AlertDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const [totalItems, setTotalItems] = useState(0);
  const [activeActionMenuId, setActiveActionMenuId] = useState<number | null>(null);
  const [actionMenuPosition, setActionMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Expanded Record Keys state for show more / show less
  // const [expandedRecordKeys, setExpandedRecordKeys] = useState<Record<number, boolean>>({});

  // const toggleExpandRecordKey = (alertId: number, e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   setExpandedRecordKeys(prev => ({
  //     ...prev,
  //     [alertId]: !prev[alertId]
  //   }));
  // };

  // Confirmation Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'acknowledge' | 'resolve';
    alertId: number;
    alertTitle?: string;
  } | null>(null);

  const openConfirmModal = (type: 'acknowledge' | 'resolve', alertId: number, alertTitle?: string) => {
    setConfirmModal({
      isOpen: true,
      type,
      alertId,
      alertTitle
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal(null);
  };

  const handleActionMenuToggle = (e: React.MouseEvent<HTMLButtonElement>, alertId: number) => {
    e.stopPropagation();
    if (activeActionMenuId === alertId) {
      setActiveActionMenuId(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuWidth = 176;
      const menuHeight = 130;

      let left = rect.right - menuWidth;
      if (left < 10) left = 10;

      let top = rect.bottom + 4;
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 4;
      }
      if (top < 10) top = 10;

      setActionMenuPosition({ top, left });
      setActiveActionMenuId(alertId);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (activeActionMenuId !== null && !target.closest('.action-menu-portal') && !target.closest('.action-menu-trigger')) {
        setActiveActionMenuId(null);
      }
    };
    const handleScroll = () => {
      if (activeActionMenuId !== null) {
        setActiveActionMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [activeActionMenuId]);

  const handleConfirmAction = async () => {
    if (!confirmModal) return;
    const { type, alertId } = confirmModal;
    closeConfirmModal();
    if (type === 'acknowledge') {
      await handleAcknowledge(alertId);
    } else if (type === 'resolve') {
      await handleResolve(alertId);
    }
  };

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params: any = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (severityFilter !== 'all') params.severity = severityFilter;
      if (dateRange !== 'all') {
        if (dateRange === 'custom') {
          if (dateFrom) {
            const utcFrom = toUTCStartOfDay(dateFrom);
            params.date_from = utcFrom;
            params.from = utcFrom;
          }
          if (dateTo) {
            const utcTo = toUTCEndOfDay(dateTo);
            params.date_to = utcTo;
            params.to = utcTo;
          }
          params.range = 'custom';
        } else {
          params.range = dateRange;
        }
      }
      const querySearch = debouncedSearch.trim();
      if (querySearch) params.search = querySearch;
      if (sortBy) {
        params.sort_by = sortBy;
        params.order = sortOrder;
      }

      const response = await automationApi.getAlerts(params);
      const resData = response.data || {};
      const rawList = resData.alerts || resData.data || (Array.isArray(resData) ? resData : []);

      const mappedList: Alert[] = rawList.map((item: any) => ({
        id: item.id,
        rule_id: item.rule_id,
        rule_name: item.rule_name || item.rule_title || `Rule #${item.rule_id}`,
        client_id: item.client_id ?? item.organization_id ?? null,
        client_name: item.client_name || item.client?.name || null,
        asset_id: item.asset_id ?? null,
        asset_name: item.asset_name || item.asset?.name || null,
        asset_os_name: item.asset_os_name || null,
        asset_os_version: item.asset_os_version || null,
        collection: item.collection || item.source || 'General',
        record_key: item.record_key || item.message || '',
        severity: item.severity || 'warning',
        status: item.status || 'open',
        occurrence_count: item.occurrence_count ?? 1,
        opened_at: item.opened_at || item.created,
        last_fired_at: item.last_fired_at || item.updated || item.created,
        created: item.created,
        resolved_at: item.resolved_at || null,
        title: item.title || item.record_key || `Alert #${item.id}`,
        message: item.message || item.record_key || '',
        source: item.collection || item.source || 'General',
      }));

      setAlerts(mappedList);
      setTotalItems(resData.total ?? resData.data?.length ?? mappedList.length);
    } catch (error) {
      console.error(error);
      setAlerts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertDetail = async (id: number) => {
    setDetailLoading(true);
    try {
      const res = await automationApi.getAlert(id);
      const data = res.data?.alert || res.data?.data || res.data;
      if (data && (data.id || data.rule_id)) {
        let parsedActionLogs: ActionLog[] = [];
        if (data.action_logs) {
          if (Array.isArray(data.action_logs)) {
            parsedActionLogs = data.action_logs;
          } else if (typeof data.action_logs === 'object') {
            parsedActionLogs = Object.entries(data.action_logs).map(([key, val]: [string, any]) => {
              if (typeof val === 'object' && val !== null) {
                const respData = val.response_data || val.response || null;
                let detailText = val.error_message || val.detail || '';
                if (!detailText && respData) {
                  if (respData.ticket_number) {
                    detailText = `Created Ticket #${respData.ticket_number}`;
                  } else if (respData.messaging_product === 'whatsapp' || respData.messages || respData.contacts) {
                    detailText = `WhatsApp notification delivered`;
                  } else if (respData.note) {
                    detailText = respData.note;
                  } else if (respData.id || respData.activityId) {
                    detailText = `Sent message (ID: ${respData.activityId || respData.id})`;
                  } else {
                    detailText = `${formatText(val.action_type || key)} completed`;
                  }
                }
                return {
                  id: val.id || key,
                  action_id: String(val.action_id || key),
                  action_type: val.action_type || key,
                  phase: val.phase || 'fire',
                  status: val.status || 'success',
                  detail: detailText || `${formatText(val.action_type || key)} completed`,
                  response: respData ? respData : '',
                  duration_ms: val.duration_ms || 0,
                  created: val.created || new Date().toISOString()
                };
              }
              return {
                id: key as any,
                action_id: key,
                action_type: key,
                phase: 'fire',
                status: 'success',
                detail: String(val),
                response: String(val),
                duration_ms: 0,
                created: new Date().toISOString()
              };
            });
          }
        }

        setDetail({
          id: data.id,
          rule_id: data.rule_id,
          rule_name: data.rule_name || `Rule #${data.rule_id}`,
          client_id: data.client_id ?? data.organization_id ?? null,
          client_name: data.client_name || null,
          asset_id: data.asset_id ?? null,
          asset_name: data.asset_name || null,
          asset_os_name: data.asset_os_name || null,
          asset_os_version: data.asset_os_version || null,
          collection: data.collection || data.source || 'General',
          record_key: data.record_key || '',
          severity: data.severity || 'warning',
          status: data.status || 'open',
          occurrence_count: data.occurrence_count ?? 1,
          opened_at: data.opened_at || data.created,
          last_fired_at: data.last_fired_at || data.updated || data.created,
          created: data.created,
          resolved_at: data.resolved_at || null,
          title: data.title || data.record_key || `Alert #${data.id}`,
          message: data.message || data.record_key || '',
          source: data.collection || data.source || 'General',
          context: data.context || {
            record_key: data.record_key,
            collection: data.collection,
            asset_id: data.asset_id,
            organization_id: data.organization_id,
            last_signal: data.last_signal
          },
          escalated_at: data.escalated_at || null,
          acknowledged_at: data.acknowledged_at || null,
          acknowledged_by: data.acknowledged_by || null,
          resolved_by: data.resolved_by || null,
          dedup_key: data.dedup_key || `${data.collection || 'alert'}:${data.asset_id || ''}:${data.record_key || ''}`,
          action_logs: parsedActionLogs,
          timeline: Array.isArray(data.timeline) ? data.timeline : [],
          flow_run: data.flow_run || null
        } as unknown as AlertDetail);
      }
    } catch (err) {
      const base = alerts.find(a => a.id === id);
      if (base) {
        setDetail({
          ...base,
          context: {
            record_key: base.record_key,
            collection: base.collection,
            asset_id: base.asset_id,
            organization_id: base.organization_id
          },
          escalated_at: null,
          acknowledged_at: null,
          acknowledged_by: null,
          resolved_by: null,
          dedup_key: `${base.collection}:${base.asset_id}:${base.record_key}`,
          action_logs: [],
          flow_run: null
        } as unknown as AlertDetail);
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Debounce search input changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSearchSubmit = () => {
    setDebouncedSearch(search);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (val: string) => {
    setStatusFilter(val);
    setCurrentPage(1);
  };

  const handleSeverityFilterChange = (val: string) => {
    setSeverityFilter(val);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (val: string) => {
    setDateRange(val);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, severityFilter, dateRange, dateFrom, dateTo]);

  useEffect(() => {
    fetchAlerts();
  }, [debouncedSearch, statusFilter, severityFilter, dateRange, dateFrom, dateTo, currentPage, itemsPerPage, sortBy, sortOrder, clientLevel]);

  const sortedAlerts = useMemo(() => {
    let result = alerts;

    if (dateRange !== 'all') {
      const now = Date.now();
      result = result.filter(alert => {
        const timeStr = alert.last_fired_at || alert.opened_at || alert.created;
        if (!timeStr) return true;
        const alertTime = new Date(timeStr).getTime();
        if (isNaN(alertTime)) return true;

        if (dateRange === 'last_24_hours') {
          return alertTime >= now - 24 * 60 * 60 * 1000;
        }
        if (dateRange === 'last_7_days') {
          return alertTime >= now - 7 * 24 * 60 * 60 * 1000;
        }
        if (dateRange === 'last_30_days') {
          return alertTime >= now - 30 * 24 * 60 * 60 * 1000;
        }
        if (dateRange === 'last_90_days') {
          return alertTime >= now - 90 * 24 * 60 * 60 * 1000;
        }
        if (dateRange === 'custom') {
          let fromPass = true;
          let toPass = true;
          if (dateFrom) {
            const fromTime = new Date(toUTCStartOfDay(dateFrom)).getTime();
            if (!isNaN(fromTime)) fromPass = alertTime >= fromTime;
          }
          if (dateTo) {
            const toTime = new Date(toUTCEndOfDay(dateTo)).getTime();
            if (!isNaN(toTime)) toPass = alertTime <= toTime;
          }
          return fromPass && toPass;
        }
        return true;
      });
    }

    const term = debouncedSearch.trim().toLowerCase();
    if (term) {
      result = result.filter(alert => {
        const ruleName = (alert.rule_name || '').toLowerCase();
        const collection = (alert.collection || alert.source || '').toLowerCase();
        const clientName = (alert.client_name || '').toLowerCase();
        const assetName = (alert.asset_name || '').toLowerCase();
        const title = (alert.title || '').toLowerCase();
        const message = (alert.message || '').toLowerCase();
        const recordKey = (alert.record_key || '').toLowerCase();
        const status = (alert.status || '').toLowerCase();
        const severity = (alert.severity || '').toLowerCase();
        const idStr = String(alert.id);

        return (
          ruleName.includes(term) ||
          collection.includes(term) ||
          clientName.includes(term) ||
          assetName.includes(term) ||
          title.includes(term) ||
          message.includes(term) ||
          recordKey.includes(term) ||
          status.includes(term) ||
          severity.includes(term) ||
          idStr.includes(term)
        );
      });
    }

    if (!sortBy) return result;
    return [...result].sort((a: any, b: any) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'rule_name' || sortBy === 'rule') {
        aVal = a.rule_name || '';
        bVal = b.rule_name || '';
      } else if (sortBy === 'trigger' || sortBy === 'source' || sortBy === 'collection') {
        aVal = a.collection || a.source || '';
        bVal = b.collection || b.source || '';
      } else if (sortBy === 'client_name' || sortBy === 'client') {
        aVal = a.client_name || '';
        bVal = b.client_name || '';
      } else if (sortBy === 'asset_name' || sortBy === 'asset') {
        aVal = a.asset_name || '';
        bVal = b.asset_name || '';
      } else if (sortBy === 'status') {
        aVal = a.status || '';
        bVal = b.status || '';
      } else if (sortBy === 'created' || sortBy === 'last_fired_at' || sortBy === 'timeline') {
        aVal = a.last_fired_at ? new Date(a.last_fired_at).getTime() : (a.created ? new Date(a.created).getTime() : 0);
        bVal = b.last_fired_at ? new Date(b.last_fired_at).getTime() : (b.created ? new Date(b.created).getTime() : 0);
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        return sortOrder === 'asc' ? (aVal === bVal ? 0 : aVal ? 1 : -1) : (aVal === bVal ? 0 : aVal ? -1 : 1);
      }
      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;
      return 0;
    });
  }, [alerts, debouncedSearch, dateRange, dateFrom, dateTo, sortBy, sortOrder]);

  const handleAcknowledge = async (id: number) => {
    try {
      const res = await automationApi.acknowledgeAlert(id);
      if (res.data?.status || res.data?.id) {
        toast.success('Alert acknowledged');
        fetchAlerts();
        if (selectedAlertId === id) fetchAlertDetail(id);
      }
    } catch (err) {
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, status: 'acknowledged' } : a));
      if (detail && detail.id === id) {
        setDetail({
          ...detail,
          status: 'acknowledged',
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: 'Current User'
        });
      }
      toast.success('Acknowledged (demo)');
    }
  };

  const handleResolve = async (id: number) => {
    try {
      const res = await automationApi.resolveAlert(id);
      if (res.data?.status || res.data?.id) {
        toast.success('Alert resolved');
        fetchAlerts();
        if (selectedAlertId === id) fetchAlertDetail(id);
      }
    } catch (err) {
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, status: 'resolved', resolved_at: new Date().toISOString() } : a));
      if (detail && detail.id === id) {
        setDetail({
          ...detail,
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          resolved_by: 'Current User'
        });
      }
      toast.success('Resolved (demo)');
    }
  };

  const handleApproveAction = async (logId: number) => {
    try {
      const res = await automationApi.approveActionLog(logId);
      if (res.data?.status) {
        toast.success('Action approved and executed');
        if (selectedAlertId) fetchAlertDetail(selectedAlertId);
      }
    } catch (err) {
      if (detail) {
        setDetail({
          ...detail,
          action_logs: (detail.action_logs || []).map(log =>
            log.id === logId
              ? { ...log, status: 'success', detail: `${log.detail} (Approved and Sent)` }
              : log
          ),
          flow_run: detail.flow_run?.current_step === 'a2' ? {
            ...detail.flow_run,
            status: 'completed',
            detail: 'Flow run completed successfully.'
          } : detail.flow_run
        });
      }
      toast.success('Approved (demo)');
    }
  };

  const openDrawer = (alertId: number) => {
    setSelectedAlertId(alertId);
    fetchAlertDetail(alertId);
  };

  const closeDrawer = () => {
    setSelectedAlertId(null);
    setDetail(null);
  };

  const getSeverityColor = (severity?: string) => {
    switch (severity) {
      case 'critical': return 'text-red-500';
      case 'warning': return 'text-yellow-500';
      default: return 'text-blue-500';
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'open': return 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20';
      case 'acknowledged': return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-500/20';
      case 'resolved': return 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20';
      case 'suppressed': return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700';
      default: return 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20';
    }
  };

  return (
    <div className="flex h-full relative overflow-hidden">
      {/* Alerts main listing */}
      <div className="flex-1 flex flex-col space-y-2.5 min-w-0 pr-0">
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-2.5 bg-white/80 dark:bg-gray-800/40 p-2.5 border border-gray-200 dark:border-gray-700/50 rounded-xl backdrop-blur-md shadow-sm dark:shadow-none">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-white bg-orange-600 p-1 rounded-lg shrink-0" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white whitespace-nowrap tracking-tight">
              Alert log
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto justify-end">
            <div className="relative w-full md:w-72">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search alert, collection, record..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-8 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setDebouncedSearch('');
                    setCurrentPage(1);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-white cursor-pointer"
                  title="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-gray-600 dark:text-gray-200 uppercase tracking-wider font-bold">Status:</span>
              <CustomSelect
                value={statusFilter}
                onChange={handleStatusFilterChange}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'open', label: 'Open' },
                  { value: 'acknowledged', label: 'Acknowledged' },
                  { value: 'resolved', label: 'Resolved' },
                  { value: 'suppressed', label: 'Suppressed' },
                ]}
              />
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-gray-600 dark:text-gray-200 uppercase tracking-wider font-bold">Severity:</span>
              <CustomSelect
                value={severityFilter}
                onChange={handleSeverityFilterChange}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'critical', label: 'Critical' },
                  { value: 'warning', label: 'Warning' },
                  { value: 'info', label: 'Info' },
                ]}
              />
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-gray-600 dark:text-gray-200 uppercase tracking-wider font-bold">Date:</span>
              <CustomSelect
                value={dateRange}
                onChange={handleDateRangeChange}
                options={[
                  { value: 'all', label: 'All Time' },
                  { value: 'last_24_hours', label: 'Last 24 Hours' },
                  { value: 'last_7_days', label: 'Last 7 Days' },
                  { value: 'last_30_days', label: 'Last 30 Days' },
                  { value: 'last_90_days', label: 'Last 90 Days' },
                  { value: 'custom', label: 'Custom' },
                ]}
              />
            </div>

            {dateRange === 'custom' && (
              <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-2.5 py-1 rounded-lg">
                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 font-mono">From:</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-gray-800 dark:text-gray-200 text-xs px-2 py-1 rounded-md focus:outline-none focus:border-orange-500 cursor-pointer"
                  />
                </div>
                <span className="text-gray-400 dark:text-gray-500 font-bold">-</span>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 font-mono">To:</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-gray-800 dark:text-gray-200 text-xs px-2 py-1 rounded-md focus:outline-none focus:border-orange-500 cursor-pointer"
                  />
                </div>
              </div>
            )}

            <button
              onClick={fetchAlerts}
              className="p-1.5 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Alert table view */}
        <div className="flex-1 overflow-x-auto min-w-full relative custom-scroll border border-gray-200 dark:border-gray-700/50 rounded-xl bg-white dark:bg-gray-800/20 backdrop-blur-md min-h-[350px] shadow-sm dark:shadow-none">
          <table className="w-full text-[11px] text-left border-collapse min-w-[1250px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-900/50 backdrop-blur-md text-gray-700 dark:text-gray-100 uppercase tracking-widest text-[10px] font-bold select-none">
                <th
                  className="px-3.5 py-2.5 min-w-[220px] cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
                  onClick={() => handleSort('rule_name')}
                >
                  <div className="flex items-center gap-1.5">
                    Rule
                    <SortIcon field="rule_name" />
                  </div>
                </th>

                <th
                  className="px-3.5 py-2.5 min-w-[120px] cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
                  onClick={() => handleSort('trigger')}
                >
                  <div className="flex items-center gap-1.5">
                    Trigger
                    <SortIcon field="trigger" />
                  </div>
                </th>
                {isGlobalLevel && (
                  <th
                    className="px-3.5 py-2.5 cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('client_name')}
                  >
                    <div className="flex items-center gap-1.5">
                      Client
                      <SortIcon field="client_name" />
                    </div>
                  </th>
                )}
                <th
                  className="px-3.5 py-2.5 min-w-[200px] cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
                  onClick={() => handleSort('asset_name')}
                >
                  <div className="flex items-center gap-1.5">
                    Asset
                    <SortIcon field="asset_name" />
                  </div>
                </th>
                <th
                  className="px-3.5 py-2.5 cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1.5">
                    Status
                    <SortIcon field="status" />
                  </div>
                </th>
                <th
                  className="px-3.5 py-2.5 cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
                  onClick={() => handleSort('last_fired_at')}
                >
                  <div className="flex items-center gap-1.5">
                    Timeline
                    <SortIcon field="last_fired_at" />
                  </div>
                </th>
                <th className="sticky right-0 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur-md z-20 px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.06)] dark:shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.5)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/70 dark:divide-gray-700/30">
              {loading ? (
                <tr>
                  <td colSpan={isGlobalLevel ? 9 : 8} className="py-16 text-center text-gray-500 dark:text-gray-200 font-bold uppercase tracking-widest italic">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                      Loading alerts...
                    </div>
                  </td>
                </tr>
              ) : sortedAlerts.length === 0 ? (
                <tr>
                  <td colSpan={isGlobalLevel ? 9 : 8} className="py-16 text-center text-gray-500 dark:text-gray-200 font-bold uppercase tracking-widest italic">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <ShieldAlert className="w-8 h-8 text-gray-400 dark:text-gray-200" />
                      <p className="text-xs">No alerts match your filter</p>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedAlerts.map((alert) => {
                  return (
                    <tr
                      key={alert.id}
                      onClick={() => openDrawer(alert.id)}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/20 border-b border-gray-200/70 dark:border-gray-800/50 transition-colors cursor-pointer group relative ${selectedAlertId === alert.id ? 'bg-orange-50/50 dark:bg-orange-500/5' : ''
                        }`}
                    >
                      {/* Rule */}
                      <td className="px-3.5 py-2.5 font-medium select-text min-w-[220px]">
                        <div className="text-[11px] text-gray-900 dark:text-gray-200 font-bold whitespace-nowrap" title={alert.rule_name}>
                          {alert.rule_name}
                        </div>
                      </td>

                      {/* Collection */}
                      <td className="px-3.5 py-2.5 font-medium select-text min-w-[220px]">
                        <span className="px-2 py-0.5 font-mono font-bold text-gray-800 dark:text-gray-200">
                          {formatText(alert.collection || "")}
                        </span>
                      </td>
                      {/* Client (Global Level Only) */}
                      {isGlobalLevel && (
                        <td className="px-3.5 py-2.5 font-medium select-text min-w-[220px]">
                          <span className="px-2 py-0.5 font-mono font-bold text-gray-800 dark:text-gray-200">
                            {alert.client_name || (alert.client_id ? `Client #${alert.client_id}` : 'Global')}
                          </span>
                        </td>
                      )}

                      {/* Asset Name */}
                      <td className="px-3.5 py-2.5 font-medium select-text min-w-[200px]">
                        <span className="text-[11px] font-mono font-bold text-gray-900 dark:text-gray-200 whitespace-nowrap">
                          {alert.asset_name || (alert.asset_id ? `#${alert.asset_id}` : '—')}
                        </span>
                      </td>

                      {/* Status & Severity */}
                      <td className="px-3.5 py-2.5 font-medium select-text">
                        <div className="flex flex-col gap-1 items-start">
                          <span className={`text-[8px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${getStatusBadge(alert.status)}`}>
                            {alert.status}
                          </span>
                        </div>
                      </td>

                      {/* Timeline */}
                      <td className="px-3.5 py-2.5 font-medium select-text whitespace-nowrap min-w-[170px]">
                        <div className="text-[10px] space-y-1 font-mono">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[9px] text-gray-500 dark:text-gray-200 font-bold uppercase w-12 tracking-wider shrink-0">Opened:</span>
                            <span className="text-gray-700 dark:text-gray-200">{formatDateTime(alert.opened_at)}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[9px] text-gray-500 dark:text-gray-200 font-bold uppercase w-12 tracking-wider shrink-0">Fired:</span>
                            <span className="text-gray-700 dark:text-gray-200">{formatDateTime(alert.last_fired_at)}</span>
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="sticky right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md px-3.5 py-2.5 text-center flex-shrink-0 shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.06)] dark:shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.5)] z-10">
                        <div className="relative inline-block text-left">
                          <button
                            onClick={(e) => handleActionMenuToggle(e, alert.id)}
                            className={`action-menu-trigger rounded-lg hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 p-1.5 flex items-center justify-center transition-all cursor-pointer ${activeActionMenuId === alert.id
                              ? 'border-blue-500 text-blue-600 dark:text-blue-400 ring-2 ring-blue-500/30'
                              : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                              }`}
                            title="Actions"
                          >
                            <CircleEllipsis className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalItems > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 py-2.5 bg-white dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 rounded-xl text-xs text-gray-700 dark:text-gray-200 flex-shrink-0 shadow-sm dark:shadow-none">
            <div className="flex items-center gap-3">
              <span>
                Showing <span className="font-semibold text-gray-900 dark:text-white">{totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> to{' '}
                <span className="font-semibold text-gray-900 dark:text-white">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                <span className="font-semibold text-gray-900 dark:text-white">{totalItems}</span> entries
              </span>
              <div className="flex items-center gap-2 border-l border-gray-200 dark:border-gray-800 pl-3">
                <span className="text-[11px] text-gray-600 dark:text-gray-200">Per page:</span>
                <ItemsPerPageDropdown
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                />
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-200 cursor-pointer"
                title="First Page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-200 cursor-pointer"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200 font-medium">
                Page <span className="text-gray-900 dark:text-white font-bold">{currentPage}</span> of <span className="text-gray-900 dark:text-white font-bold">{totalPages || 1}</span>
              </span>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-200 cursor-pointer"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-200 cursor-pointer"
                title="Last Page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Side drawer for Alert details */}
      {selectedAlertId && (
        <div className="absolute inset-y-0 right-0 w-full sm:w-128 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700/70 z-30 shadow-2xl flex flex-col transition-all duration-300">
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700/60 bg-gray-50/80 dark:bg-gray-950/40">
            <div className="flex items-center gap-1.5">
              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${detail ? getStatusBadge(detail.status) : ''}`}>
                {detail?.status}
              </span>
              <h3 className="text-xs font-bold text-gray-900 dark:text-white">Alert #{selectedAlertId} Details</h3>
            </div>
            <button
              onClick={closeDrawer}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {detailLoading || !detail ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto custom-scroll p-3 space-y-4">
              {/* Header Info */}
              <div className="flex items-center justify-between gap-3">
                <div
                  onClick={() => detail.rule_id && navigate(`/automation/editor/${detail.rule_id}`)}
                  className="text-[11px] text-orange-600 dark:text-orange-400 font-bold hover:text-orange-500 dark:hover:text-orange-300 hover:underline cursor-pointer inline-flex items-center gap-1 group truncate min-w-0"
                  title={`Open Rule #${detail.rule_id} in Editor`}
                >
                  <span className="truncate">{detail.rule_name}</span>
                  <ExternalLink className="w-3 h-3 text-orange-600 dark:text-orange-400 group-hover:text-orange-500 dark:group-hover:text-orange-300 shrink-0" />
                </div>

                {detail.status !== 'resolved' && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    {detail.status === 'open' && (
                      <button
                        onClick={() => openConfirmModal('acknowledge', detail.id, detail.rule_name)}
                        className="flex items-center gap-1 px-2.5 py-1 bg-yellow-500/10 hover:bg-yellow-500/20 dark:bg-yellow-500/20 dark:hover:bg-yellow-500/30 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30 rounded-lg text-[10px] font-bold transition-all duration-150 cursor-pointer"
                      >
                        <Clock className="w-3 h-3" />
                        ACKNOWLEDGE
                      </button>
                    )}
                    <button
                      onClick={() => openConfirmModal('resolve', detail.id, detail.rule_name)}
                      className="flex items-center gap-1 px-2.5 py-1 bg-green-500/10 hover:bg-green-500/20 dark:bg-green-500/20 dark:hover:bg-green-500/30 text-green-600 dark:text-green-400 border border-green-500/30 rounded-lg text-[10px] font-bold transition-all duration-150 cursor-pointer"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      RESOLVE
                    </button>
                  </div>
                )}
              </div>

              {/* Alert properties Grid */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 dark:bg-gray-800/20 p-2.5 rounded-lg border border-gray-200 dark:border-gray-800/40 space-y-0.5">
                  <span className="text-[9px] text-gray-500 dark:text-gray-200 uppercase tracking-wider font-bold block">Severity</span>
                  <div className={`text-[11px] font-bold capitalize ${getSeverityColor(detail.severity)}`}>{detail.severity || 'warning'}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/20 p-2.5 rounded-lg border border-gray-200 dark:border-gray-800/40 space-y-0.5">
                  <span className="text-[9px] text-gray-500 dark:text-gray-200 uppercase tracking-wider font-bold block">Collection</span>
                  <div className="text-[11px] font-mono font-bold text-cyan-600 dark:text-cyan-400">{formatText(detail.collection || "") || 'General'}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/20 p-2.5 rounded-lg border border-gray-200 dark:border-gray-800/40 space-y-0.5">
                  <span className="text-[9px] text-gray-500 dark:text-gray-200 uppercase tracking-wider font-bold block">Asset</span>
                  <div className="text-[11px] font-mono font-bold text-indigo-600 dark:text-indigo-300">
                    {detail.asset_name || (detail.asset_id ? `#${detail.asset_id}` : "-")}
                  </div>
                  {detail.asset_os_name && (
                    <div className="text-[9px] text-gray-500 dark:text-gray-200 font-mono">{detail.asset_os_name}</div>
                  )}
                </div>
                {isGlobalLevel && (
                  <div className="bg-gray-50 dark:bg-gray-800/20 p-2.5 rounded-lg border border-gray-200 dark:border-gray-800/40 space-y-0.5">
                    <span className="text-[9px] text-gray-500 dark:text-gray-200 uppercase tracking-wider font-bold block">Client</span>
                    <div className="text-[11px] font-mono font-bold text-orange-600 dark:text-orange-300">
                      {detail.client_name || (detail.client_id ? `Client #${detail.client_id}` : 'Global')}
                    </div>
                  </div>
                )}
                <div className="bg-gray-50 dark:bg-gray-800/20 p-2.5 rounded-lg border border-gray-200 dark:border-gray-800/40 space-y-0.5">
                  <span className="text-[9px] text-gray-500 dark:text-gray-200 uppercase tracking-wider font-bold block">Occurrences</span>
                  <div className="text-[11px] font-semibold text-gray-900 dark:text-white">{detail.occurrence_count} times</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/20 p-2.5 rounded-lg border border-gray-200 dark:border-gray-800/40 space-y-0.5">
                  <span className="text-[9px] text-gray-500 dark:text-gray-200 uppercase tracking-wider font-bold block">Opened At</span>
                  <div className="text-[10px] text-gray-600 dark:text-gray-200 font-mono">{formatDateTime(detail.opened_at)}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/20 p-2.5 rounded-lg border border-gray-200 dark:border-gray-800/40 space-y-0.5">
                  <span className="text-[9px] text-gray-500 dark:text-gray-200 uppercase tracking-wider font-bold block">Last Fired At</span>
                  <div className="text-[10px] text-gray-600 dark:text-gray-200 font-mono">{formatDateTime(detail.last_fired_at)}</div>
                </div>
              </div>

              {/* Execution flow run status */}
              {detail.flow_run && (
                <div className="bg-orange-50/60 dark:bg-orange-500/5 border border-orange-200 dark:border-orange-500/20 rounded-xl p-3 space-y-1.5">
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 dark:bg-orange-400 animate-pulse"></span>
                    <h5 className="text-[10px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider">Flow Chain Run Status</h5>
                  </div>
                  <div className="text-[11px] text-gray-900 dark:text-white">
                    Status: <strong className="text-orange-600 dark:text-orange-300 capitalize">{detail.flow_run.status}</strong>
                  </div>
                  <div className="text-[11px] text-gray-800 dark:text-gray-200 leading-relaxed font-mono bg-white dark:bg-gray-950/40 p-2 rounded border border-orange-100 dark:border-gray-800">
                    Current Action: {detail.flow_run.current_step} <br />
                    Detail: {renderSafeContent(detail.flow_run.detail)}
                  </div>
                </div>
              )}

              {/* Alert Lifecycle Timeline */}
              {renderAlertTimeline(detail.timeline)}

              {/* Action logs & Timeline (Approval Gates) */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <h5 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                    Action Delivery Logs ({detail.action_logs?.length || 0})
                  </h5>
                </div>
                {detail.action_logs && detail.action_logs.length > 0 ? (
                  <div className="space-y-2.5">
                    {detail.action_logs.map((log) => (
                      <div key={log.id} className="bg-gray-50 dark:bg-slate-900/60 p-3 rounded-xl border border-gray-200 dark:border-slate-800 hover:border-gray-300 dark:hover:border-slate-700/80 transition-all shadow-sm dark:shadow-md flex items-start justify-between gap-3">
                        <div className="space-y-1 flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="w-5 h-5 rounded-md bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 flex items-center justify-center p-0.5 shrink-0 shadow-inner">
                              {getActionLogIcon(log.action_type)}
                            </div>
                            <span className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wide">{getActionFormattedTitle(log.action_type)}</span>
                            <span className={`text-[8px] uppercase font-bold px-2 py-0.5 rounded-full border ${log.status === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400' :
                              log.status === 'pending_approval' ? 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400' :
                                log.status === 'skipped' ? 'bg-gray-200 dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400' :
                                  'bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400'
                              }`}>
                              {log.status.replace('_', ' ')}
                            </span>
                            <span className="text-[9px] text-gray-500 dark:text-gray-400 font-medium px-1.5 py-0.5 rounded bg-white dark:bg-gray-950/60 border border-gray-200 dark:border-gray-800/60">{log.phase} phase</span>
                          </div>
                          {log.detail && (
                            <p className="text-[11px] text-gray-800 dark:text-gray-200 leading-relaxed font-medium pt-0.5">{log.detail}</p>
                          )}
                          {renderCleanLogResponse(log.response)}
                          <div className="text-[9px] text-gray-500 dark:text-gray-400 pt-1 flex items-center gap-2">
                            <span>{formatDateTime(log.created)}</span>
                            {log.duration_ms > 0 && <span>• {log.duration_ms}ms</span>}
                          </div>
                        </div>

                        {/* Approval Action Button */}
                        {log.status === 'pending_approval' && (
                          <button
                            onClick={() => handleApproveAction(log.id)}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-[10px] font-bold shadow-md transition-all active:scale-95 flex-shrink-0 mt-1 cursor-pointer"
                          >
                            <Lock className="w-3.5 h-3.5" />
                            Approve
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-900/40 p-3 rounded-xl border border-gray-200 dark:border-gray-800 text-center">
                    No action logs recorded for this alert execution.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Custom Confirmation Modal */}
      {confirmModal && confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 max-w-md w-full shadow-2xl space-y-4 relative">
            <div className="flex items-start gap-3.5">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${confirmModal.type === 'acknowledge'
                  ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-600 dark:text-yellow-400'
                  : 'bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400'
                  }`}
              >
                {confirmModal.type === 'acknowledge' ? (
                  <Clock className="w-5 h-5" />
                ) : (
                  <CheckCircle2 className="w-5 h-5" />
                )}
              </div>
              <div className="space-y-1 flex-1">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide">
                  {confirmModal.type === 'acknowledge' ? 'Confirm Acknowledge Alert' : 'Confirm Resolve Alert'}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-200 leading-relaxed">
                  Are you sure you want to {confirmModal.type === 'acknowledge' ? 'acknowledge' : 'resolve'} alert{' '}
                  <span className="font-mono font-bold text-orange-600 dark:text-orange-400">#{confirmModal.alertId}</span>
                  {confirmModal.alertTitle ? ` (${confirmModal.alertTitle})` : ''}?
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-gray-200 dark:border-gray-800">
              <button
                type="button"
                onClick={closeConfirmModal}
                className="px-3.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white text-xs font-semibold transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                className={`px-4 py-1.5 rounded-lg text-white text-xs font-bold transition-all shadow-md active:scale-95 cursor-pointer flex items-center gap-1.5 ${confirmModal.type === 'acknowledge'
                  ? 'bg-yellow-600 hover:bg-yellow-500 shadow-yellow-900/20'
                  : 'bg-green-600 hover:bg-green-500 shadow-green-900/20'
                  }`}
              >
                {confirmModal.type === 'acknowledge' ? (
                  <>
                    <Clock className="w-3.5 h-3.5" />
                    Yes, Acknowledge
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Yes, Resolve
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions Portal Menu */}
      {activeActionMenuId && (() => {
        const activeAlert = alerts.find(a => a.id === activeActionMenuId);
        if (!activeAlert) return null;
        return createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998] cursor-default"
              onClick={(e) => {
                e.stopPropagation();
                setActiveActionMenuId(null);
              }}
            />
            <div
              className="fixed w-44 bg-white dark:bg-[#0d1527] border border-gray-200 dark:border-gray-800/90 rounded-xl shadow-2xl overflow-hidden py-1 z-[9999] text-left action-menu-portal"
              style={{
                top: actionMenuPosition.top,
                left: actionMenuPosition.left,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const alertId = activeAlert.id;
                  setActiveActionMenuId(null);
                  openDrawer(alertId);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors cursor-pointer"
              >
                <Eye className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                <span>View Details</span>
              </button>

              {activeAlert.status === 'open' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const alertId = activeAlert.id;
                    const ruleName = activeAlert.rule_name;
                    setActiveActionMenuId(null);
                    openConfirmModal('acknowledge', alertId, ruleName);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300 hover:bg-yellow-500/10 transition-colors cursor-pointer"
                >
                  <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                  <span>Acknowledge</span>
                </button>
              )}

              {activeAlert.status !== 'resolved' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const alertId = activeAlert.id;
                    const ruleName = activeAlert.rule_name;
                    setActiveActionMenuId(null);
                    openConfirmModal('resolve', alertId, ruleName);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors cursor-pointer"
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <span>Resolve Alert</span>
                </button>
              )}
            </div>
          </>,
          document.body
        );
      })()}
    </div>
  );
};
